import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Alert } from '@buildoutinc/blueprint-react/ui/Alert';
import { ButtonGroup } from '@buildoutinc/blueprint-react/ui/ButtonGroup';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEnvelope, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useMediaQuery } from 'react-responsive';
import { v4 as uuid } from 'uuid';

import { Button } from 'components/ui/button';
import { post } from 'helpers/api';
import { parseWithFormats } from 'helpers/date_utils';
import { formatDate, ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';
import { recordChanged } from 'helpers/record_changed';

import EmailPanel from './email_panel';
import ActivityPanel from './activity_panel';
import { CALL_RESULTS, LOG_ACTIVITY_TABS, PHONE_VERIFYING_CALL_RESULTS, placeholders, purposes } from './config';
import { saveActivity } from '../form/helpers';
import { refreshActivities } from 'bundles/apex/activities/hooks';
import ReauthModal from '../send_email/reauth_modal';
import {
  buildRequestBody, clearPendingReply, errorMessage, hasPendingReply, shouldAutoOpenCompose, splitEmails,
  takeEmailDraft,
} from '../send_email/helpers';

import type { EmailFormValues } from './email_panel';
import type { Recipient } from './email_recipient';
import type { Activity } from 'bundles/apex/activities/types';
import type { LogActivityType } from './types';
import type { ApiError, EmailFile, SendEmailConfig } from '../send_email/types';
import { showSnackbar } from 'components/ui/snackbar/helper';

const EMPTY_EMAIL: EmailFormValues = { bcc: '', body: '', cc: '', subject: '', to: [] };
const GENERIC_ERROR = 'Something went wrong. Please try again.';
const ICON_ONLY_TABS_MAX_WIDTH_PX = 1535;

const tagsBody = (tags: Array<Record<string, unknown>>) => tags.map(tag => `[[${JSON.stringify(tag)}]] `).join('');

export interface LogActivityProps {
  aiEnabled?: boolean;
  parentId?: number;
  parentType?: string;
  path: string;
  subjectName?: string;
  fromEmail?: string;
  sendEmail?: SendEmailConfig;
  tagsPath?: string;
  // Starts a new activity tagging these, in the input tagsPath swaps in.
  seedTags?: Array<Record<string, unknown>>;
  activity?: Activity;
  onLogged: (activity: Activity) => void;
  onClose?: () => void;
}

export default function LogActivity({
  aiEnabled,
  parentId,
  parentType,
  path,
  subjectName,
  fromEmail,
  sendEmail,
  tagsPath,
  seedTags,
  activity,
  onLogged,
  onClose,
}: LogActivityProps) {
  const editing = Boolean(activity);

  // Read during render: Activities' mount effect strips the return flag and takes the parked reply.
  const [activeTab, setActiveTab] = useState<LogActivityType>(() => {
    if (activity?.activityType) return activity.activityType as LogActivityType;

    const returnedToCompose = shouldAutoOpenCompose(window.location.search, sendEmail);
    return returnedToCompose && !hasPendingReply(sendEmail?.contactId) ? 'message' : 'note';
  });
  const [showReauthModal, setShowReauthModal] = useState(false);
  const promptedToReconnect = useRef(false);
  const seeded = Boolean(!activity && tagsPath && seedTags?.length);
  const [body, setBody] = useState(() => activity?.body ?? (seeded ? tagsBody(seedTags!) : ''));
  const bodyParts = activity?.bodyParts ?? (seeded ? seedTags : undefined);
  const [date, setDate] = useState<Date>(() => (
    activity?.date ? parseWithFormats(activity.date, ISO_DATE_FORMAT) : new Date()
  ));
  const [callResult, setCallResult] = useState('');
  const [emailFiles, setEmailFiles] = useState<EmailFile[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuid());
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const iconOnlyTabs = useMediaQuery({ maxWidth: ICON_ONLY_TABS_MAX_WIDTH_PX });

  const canSendEmail = Boolean(sendEmail?.enabled && sendEmail.canSend && sendEmail.path);
  const mustReconnectToSend = Boolean(sendEmail?.enabled && sendEmail.path) && !canSendEmail;

  const contactEmails = useMemo(() => (sendEmail?.contactEmails ?? []).map(
    ({ email, primary }): Recipient => ({ email, name: subjectName, primary })
  ), [sendEmail, subjectName]);
  const buildEmail = useCallback((): EmailFormValues => {
    const prefill = contactEmails.find(recipient => recipient.primary) ?? contactEmails[0];
    return { ...EMPTY_EMAIL, to: prefill ? [prefill] : [] };
  }, [contactEmails]);
  const [email, setEmail] = useState<EmailFormValues>(buildEmail);

  useEffect(() => {
    // An email the assistant drafted and the broker chose to edit rather than send as written.
    void takeEmailDraft(sendEmail).then((draft) => {
      if (draft) {
        setActiveTab('message');
        // Recipients come from `buildEmail` rather than the draft, so they start on the contact's
        // primary address the way every other email on this page does.
        setEmail({ ...buildEmail(), body: draft.body, subject: draft.subject });
        setEmailFiles(draft.attachments);
      }
    });
  }, []);

  async function logActivity(activityType: LogActivityType, body: string) {
    if (!body.trim() || saving) return;

    setErrors([]);
    setSaving(true);

    const loggedCallResult = activityType === 'call' && !editing ? callResult : '';

    try {
      const savedActivity: Activity = await saveActivity({
        activityType,
        body,
        date: formatDate(date, ISO_DATE_FORMAT),
        path,
        ...(activity?.id ? { id: activity.id } : {}),
        ...(loggedCallResult ? { callResult: loggedCallResult } : {}),
      });
      onLogged(savedActivity);
      if (parentType === 'Backoffice::Contact' && parentId && PHONE_VERIFYING_CALL_RESULTS.includes(loggedCallResult)) {
        recordChanged({ kind: 'contact_updated', id: parentId });
      }
      showSnackbar({
        text: `Activity Successfully ${editing ? 'Updated' : 'Logged'}`,
        variant: 'success',
      });

      if (editing) {
        onClose?.();
      } else {
        setBody('');
        setDate(new Date());
        setEmail(buildEmail());
        setCallResult('');
        // A fresh key marks a fresh draft, collapsing any instruction row the saved one left open.
        setIdempotencyKey(uuid());
      }
    } catch (error) {
      setErrors(Array.isArray(error) ? error : [GENERIC_ERROR]);
    } finally {
      setSaving(false);
    }
  }

  async function sendEmailActivity() {
    if (!sendEmail?.path || sendingEmail || email.to.length === 0) return;

    setErrors([]);
    setSendingEmail(true);

    try {
      const payload = {
        to: email.to.map(recipient => recipient.email),
        cc: splitEmails(email.cc),
        bcc: splitEmails(email.bcc),
        subject: email.subject,
        body: email.body,
      };
      await post(sendEmail.path, buildRequestBody(payload, emailFiles, idempotencyKey));

      refreshActivities({ fresh: true });
      showSnackbar({ text: 'Email sent', variant: 'success', icon: faEnvelope });

      setEmail(buildEmail());
      setEmailFiles([]);
      setIdempotencyKey(uuid());
      onClose?.();
    } catch (error) {
      setErrors([errorMessage(error as ApiError)]);
    } finally {
      setSendingEmail(false);
    }
  }

  const renderActivityLogBody = () => {
    switch (activeTab) {
    case 'call':
      return (
        <ActivityPanel
          aiEnabled={aiEnabled}
          bodyParts={bodyParts}
          buttonLabel={editing ? 'Save Activity' : 'Log Call'}
          collapseKey={idempotencyKey}
          date={date}
          parentId={parentId}
          parentType={parentType}
          placeholder={placeholders.call(subjectName)}
          purpose={purposes.call}
          saving={saving}
          seeded={seeded}
          tagsPath={tagsPath}
          value={body}
          onChange={setBody}
          onDateChange={setDate}
          onSubmit={() => logActivity('call', body)}
        >
          {!editing && (
            <ButtonGroup>
              {CALL_RESULTS.map((result) => {
                const selected = callResult === result.key;
                return (
                  <Button
                    icon={selected ? faCheck : null}
                    key={result.key}
                    variant={selected ? 'primary' : 'outline'}
                    onClick={() => setCallResult(selected ? '' : result.key)}
                  >
                    {result.label}
                  </Button>
                );
              })}
            </ButtonGroup>
          )}
        </ActivityPanel>
      );
    case 'message':
      return canSendEmail ? (
        <EmailPanel
          aiEnabled={aiEnabled}
          // A fresh idempotency key means a fresh draft, which is the moment to collapse the
          // instruction row the previous draft left open.
          collapseKey={idempotencyKey}
          files={emailFiles}
          fromEmail={fromEmail}
          parentId={parentId}
          parentType={parentType}
          sending={sendingEmail}
          suggestions={contactEmails}
          value={email}
          onAddFiles={files => setEmailFiles(prev => [...prev, ...files])}
          onChange={handleEmailChange}
          onRemoveFile={index => setEmailFiles(prev => prev.filter((_, i) => i !== index))}
          onSend={sendEmailActivity}
        />
      ) : (
        <ActivityPanel
          aiEnabled={aiEnabled}
          bodyParts={bodyParts}
          buttonLabel={editing ? 'Save Activity' : 'Log Email'}
          collapseKey={idempotencyKey}
          date={date}
          parentId={parentId}
          parentType={parentType}
          placeholder={placeholders.email(subjectName)}
          purpose={purposes.message}
          saving={saving}
          seeded={seeded}
          tagsPath={tagsPath}
          value={body}
          onChange={setBody}
          onDateChange={setDate}
          onSubmit={() => logActivity('message', body)}
        />
      );
    case 'note':
    case 'showing':
    case 'meeting':
      return (
        <ActivityPanel
          aiEnabled={aiEnabled}
          bodyParts={bodyParts}
          buttonLabel={editing ? 'Save Activity' : activeTab === 'note' ? 'Log Note' :
            `Log ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`
          }
          collapseKey={idempotencyKey}
          date={date}
          parentId={parentId}
          parentType={parentType}
          placeholder={placeholders.note(subjectName)}
          purpose={purposes[activeTab]}
          saving={saving}
          seeded={seeded}
          tagsPath={tagsPath}
          value={body}
          onChange={setBody}
          onDateChange={setDate}
          onSubmit={() => logActivity(activeTab, body)}
        />
      );
    }
  };

  function pickTab(tab: LogActivityType) {
    setActiveTab(tab);
    if (tab === 'message' && mustReconnectToSend && !promptedToReconnect.current) {
      promptedToReconnect.current = true;
      clearPendingReply();
      setShowReauthModal(true);
    }
  }

  function handleEmailChange(changes: Partial<EmailFormValues>) {
    setEmail(prev => ({ ...prev, ...changes }));
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
        <h5 className="fs-6 fw-semibold m-0">{editing ? 'Edit Activity' : 'Log Activity'}</h5>
        <div className="d-flex align-items-center gap-2">
          <Tabs value={activeTab}>
            <TabsList variant="pills">
              {LOG_ACTIVITY_TABS.map((tab) => {
                const iconOnly = iconOnlyTabs && tab.key !== activeTab;
                return (
                  <Tooltip disabled={!iconOnly} key={tab.key}>
                    <Tooltip.Trigger
                      delay={0}
                      render={(
                        <Tab
                          aria-label={iconOnly ? tab.label : undefined}
                          value={tab.key}
                          onClick={() => pickTab(tab.key)}
                        >
                          <span className="d-flex align-items-center gap-1">
                            <FontAwesomeIcon icon={tab.icon} />
                            {!iconOnly && tab.label}
                          </span>
                        </Tab>
                      )}
                    />
                    <Tooltip.Content>{tab.label}</Tooltip.Content>
                  </Tooltip>
                );
              })}
            </TabsList>
          </Tabs>
          {onClose && <Button aria-label="Close" icon={faXmark} variant="ghost" onClick={onClose} />}
        </div>
      </div>

      {errors.length > 0 && (
        <Alert severity="destructive">
          {errors.map((error, i) => <div key={i}>{error}</div>)}
        </Alert>
      )}

      {renderActivityLogBody()}

      {showReauthModal && sendEmail?.contactId && (
        <ReauthModal
          authPath={sendEmail.authPath}
          connected={sendEmail.connected}
          contactId={sendEmail.contactId}
          expired={sendEmail.expired}
          providerName={sendEmail.providerName}
          onClose={() => setShowReauthModal(false)}
        />
      )}
    </div>
  );
}
